import React, { useCallback, useEffect, useState } from 'react';

import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

import { UsersPreferenceData } from 'src/store/interface';
import { getPageName } from 'src/utils/analytics';
import { mixpanelObject } from 'src/utils/analytics/mixpanel';
import {
  clearCtaEventsProperty,
  settingsPropertySetter
} from 'src/utils/analytics/mixpanel/mixpanelHelper';
import {
  disableBackgroundScrolling,
  enableBackgroundScrolling
} from 'src/utils/common';
import { ConsumptionPageStringEnums } from 'src/utils/constants/ConsumptionPagesConstants';
import {
  CONTENT_LANGUAGES_COOKIE,
  DISPLAY_LANGUAGE_COOKIE
} from 'src/utils/constants/cookieConstants';
import { LanguagesPopupEnums } from 'src/utils/constants/stringConstants';

import { useAppStore } from 'src/hooks/useAppStore';
import { putLanguageData } from '../../services/apiRequests/LanguageService';
import { getTranslations } from '../../services/apiRequests/TranslationsService';
import SimpleStore from '../../store/SimpleStore';
import {
  MIXPANEL_BUTTON_TYPE,
  MIXPANEL_ELEMENT,
  MIXPANEL_HEADER,
  MIXPANEL_HORIZONTAL_INDEX,
  MIXPANEL_OLD_APP_LANGUAGE,
  MIXPANEL_OLD_CONTENT_LANGUAGE,
  MIXPANEL_PAGE_NAME
} from '../../utils/analytics/mixpanel/mixpanelConstants';
import {
  contentLanguageChangedEvent,
  ctaEvent,
  displayLanguageChangedEvent,
  timeEvent
} from '../../utils/analytics/mixpanel/mixpanelEventsHelper';
import {
  getLocalStorage,
  setCookie,
  setLocalStorage
} from '../../utils/browserStorageHandler';
import { HTTP_URL_PARAMS } from '../../utils/constants/HttpMethodsConstants';
import {
  SETTINGS,
  USER_TOKEN
} from '../../utils/constants/localStorageConstants';
import Portal from '../portal/Portal';
import ContentLanguage from './contentLanguage/ContentLanguage';
import DisplayLanguage from './displayLanguage/DisplayLanguage';
import * as style from './LanguagePopupStyles';
import {
  BellIcon,
  Button,
  ButttonContainer,
  Container,
  LanguageBody,
  LanguageButton,
  LanguageFooter,
  LanguageHeader,
  NotificationContainer,
  NotificationText,
  OutlineButton
} from './LanguagePopupStyles';

const LanguageToolTip = dynamic(() => import('../LanguageToolTip'), {
  ssr: false
});

type NotifiactionProps = {
  text: string;
};
const NotifyToUsePrimaryLanguages = ({ text }: NotifiactionProps) => {
  return (
    <NotificationContainer>
      <BellIcon />
      <NotificationText>{text}</NotificationText>
    </NotificationContainer>
  );
};

const LanguagePopup = () => {
  //used for changing language route
  const router = useRouter();

  //Hooks
  const [showContentLanguageSelection, setShowContentLanguageSelection] =
    useState(true);
  const [showNotification, setShowNotification] = useState(false);
  // const [showLanguagePopup, setShowLanguagePopup] = useState(false);
  const [disableApplyButton, setDisableApplyButton] = useState(false);
  const [updateSelection, setUpdateSelection] = useState(false);

  const {
    setTranslations,
    translations,
    usersPreferenceData,
    initialLaunchData,
    setUsersPreferenceData,
    popups,
    setPopups
  } = useAppStore();

  const showLanguagePopup: any = popups.showLanguagePopup;
  const setShowLanguagePopup = (state: boolean) => {
    setPopups({ ...popups, showLanguagePopup: state });
  };
  const languageStrings = translations?.common;
  const notificationText: string = languageStrings.BETTER_EXPERIENCE;
  const primaryLanguages =
    initialLaunchData?.getlang?.content?.filter(
      (language: { category: string }) => language.category === '1'
    ) || [];

  //settings
  const token = getLocalStorage(USER_TOKEN);
  const userPreferedLangSetting =
    usersPreferenceData || getLocalStorage(SETTINGS);

  const getSavedSettings = () => {
    return usersPreferenceData || getLocalStorage(SETTINGS);
  };

  // setting content and displaylanguages
  const savedContentLanguagesList =
    userPreferedLangSetting?.content_language?.split(',').sort() || [];
  const savedDisplayLanguage = userPreferedLangSetting?.display_language;
  const [selectedDisplayLanguage, setSelectedDisplayLanguage] =
    useState<string>(savedDisplayLanguage || 'en');
  const [selectedContentLanguages, setSelectedContentLanguage] = useState<
    string[]
  >(savedContentLanguagesList);

  const languageBtnClickMixpanelEvent = () => {
    // mixpanel Initailization start
    const appHeaderIndex = '0';
    clearCtaEventsProperty(mixpanelObject);
    mixpanelObject.setProperty(
      MIXPANEL_ELEMENT,
      ConsumptionPageStringEnums.LANGUAGE
    );
    mixpanelObject.setProperty(MIXPANEL_BUTTON_TYPE, MIXPANEL_HEADER);
    mixpanelObject.setProperty(MIXPANEL_HORIZONTAL_INDEX, appHeaderIndex);
    mixpanelObject.setProperty(MIXPANEL_PAGE_NAME, getPageName(router.asPath));
    ctaEvent();
    // mixpanel Initailization end
  };

  useEffect(() => {
    if (popups.showLanguagePopup) {
      languageBtnClickMixpanelEvent();
    }
  }, [popups.showLanguagePopup]);

  /**
   * Changes the translation of the page and the url
   */
  useEffect(() => {
    if (updateSelection) {
      (async () => {
        const translations = await getTranslations(selectedDisplayLanguage);
        setTranslations(translations);
        const { pathname } = router;
        // change just the locale and maintain all other route information including href's query
        router.push({ pathname: pathname, query: router.query }, undefined, {
          locale: selectedDisplayLanguage
        });
        setCookie(DISPLAY_LANGUAGE_COOKIE, selectedDisplayLanguage);
        SimpleStore.set(
          HTTP_URL_PARAMS.DISPLAY_LANGUAGE,
          selectedDisplayLanguage
        );
        setUpdateSelection(false);
        setShowLanguagePopup(false);
      })();
    }
  }, [updateSelection]);

  //mixpanel helpers
  const trackDisplayLanguageMixpanelEvents = (
    userSettings: UsersPreferenceData,
    previousUserSettings: UsersPreferenceData
  ) => {
    mixpanelObject.setProperty(
      MIXPANEL_OLD_APP_LANGUAGE,
      previousUserSettings.display_language
    );
    settingsPropertySetter(mixpanelObject, userSettings);
    displayLanguageChangedEvent();
  };

  const displayLanguageChanged = (
    userSettings: UsersPreferenceData,
    previousUserSettings: UsersPreferenceData
  ) => {
    if (
      userSettings.display_language !== previousUserSettings.display_language
    ) {
      previousUserSettings.display_language = userSettings.display_language;
      trackDisplayLanguageMixpanelEvents(userSettings, previousUserSettings);
    }
  };

  /**
   * Checks the equality of two arrays
   * @param {string[]} a
   * @param {string[]} b
   * @returns {boolean} isEqual
   */
  const arrayEquality = (a: string[], b: string[]) => {
    return a.length !== b.length
      ? false
      : a.every((value, index) => value === b[index]);
  };

  const trackContentLanguageChangeMixpanelEvents = (
    userSettings: UsersPreferenceData,
    previousUserSettings: UsersPreferenceData
  ) => {
    mixpanelObject.setProperty(
      MIXPANEL_OLD_CONTENT_LANGUAGE,
      previousUserSettings?.content_language?.split(',').sort()
    );
    settingsPropertySetter(mixpanelObject, userSettings);
    contentLanguageChangedEvent();
    timeEvent();
  };

  const contentLanguageChanged = (
    userSettings: UsersPreferenceData,
    previousUserSettings: UsersPreferenceData
  ) => {
    const prevContentLanguages =
      previousUserSettings?.content_language?.split(',').sort() || [];
    const currentContentLanguages =
      userSettings?.content_language?.split(',').sort() || [];
    if (!arrayEquality(prevContentLanguages, currentContentLanguages)) {
      trackContentLanguageChangeMixpanelEvents(
        userSettings,
        previousUserSettings
      );
    }
  };

  const showDisplayMenu = () => {
    setShowContentLanguageSelection(false);
  };

  const showContentMenu = () => {
    setShowContentLanguageSelection(true);
  };

  const LanguagePopupCloseHandler = () => {
    //clear states on closing
    setShowNotification(false);
    setSelectedContentLanguage(savedContentLanguagesList);
    console.log('amit: setting display lang', savedDisplayLanguage);
    setSelectedDisplayLanguage(savedDisplayLanguage);
    //close the popup
    setShowLanguagePopup(false);
  };

  const handleCheckBoxChange = useCallback((languages: string[]) => {
    if (languages.length === 0) setDisableApplyButton(true);
    else setDisableApplyButton(false);
    setSelectedContentLanguage(languages);
  }, []);

  const handleSelectionChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    name: string
  ) => {
    setSelectedDisplayLanguage(name);
  };

  const saveLanguageSettings = (type: string, setting: string) => {
    const userSettings = usersPreferenceData || getLocalStorage(SETTINGS);
    const newUserSettings = { ...userSettings, [type]: setting };
    type === LanguagesPopupEnums.DISPLAY_LANGUAGE
      ? displayLanguageChanged(newUserSettings, userSettings)
      : contentLanguageChanged(newUserSettings, userSettings);
    setCookie(
      type === LanguagesPopupEnums.DISPLAY_LANGUAGE
        ? DISPLAY_LANGUAGE_COOKIE
        : CONTENT_LANGUAGES_COOKIE,
      setting
    );
    setLocalStorage(SETTINGS, newUserSettings);
    setUsersPreferenceData({ ...newUserSettings });
    SimpleStore.set(
      HTTP_URL_PARAMS.DISPLAY_LANGUAGE,
      newUserSettings.display_language
    );
    SimpleStore.set(
      HTTP_URL_PARAMS.CONTENT_LANGUAGE,
      newUserSettings.content_language
    );
  };

  //changes the content language based on user is logged in or not
  const setContentLanguage = () => {
    if (selectedContentLanguages) {
      const contentLanguage = selectedContentLanguages.sort().join(','); //'hindi,english'
      if (token && contentLanguage !== getSavedSettings().content_language) {
        //handle settings update by login
        putLanguageData(LanguagesPopupEnums.CONTENT_LANGAUGE, contentLanguage);
      }
      saveLanguageSettings(
        LanguagesPopupEnums.CONTENT_LANGAUGE,
        contentLanguage
      );

      setUpdateSelection(true);
    }
  };
  // saves the display language based on the user is logged in or not
  const setDisplayLanguage = () => {
    if (selectedDisplayLanguage) {
      if (
        token &&
        getSavedSettings().display_language !== selectedDisplayLanguage
      ) {
        putLanguageData(
          LanguagesPopupEnums.DISPLAY_LANGUAGE,
          selectedDisplayLanguage
        );
      }
      saveLanguageSettings(
        LanguagesPopupEnums.DISPLAY_LANGUAGE,
        selectedDisplayLanguage
      );
    }
  };

  /**
   * Handles apply button logic
   * if display language is show
   * first shift to content language and apply changes
   */
  const handleApplyButton = () => {
    if (!showContentLanguageSelection) {
      //check whether the display language is selected and add the same to content language
      setSelectedContentLanguage((previousState) =>
        previousState.find((language) => language === selectedDisplayLanguage)
          ? [...previousState]
          : [...previousState, selectedDisplayLanguage].sort()
      );
      setShowContentLanguageSelection(true);
    } else {
      //Change the contentLanguage
      //check for the selected languages
      if (
        selectedContentLanguages.every(
          (language) =>
            !primaryLanguages.find(
              (primaryLanguage: { l_code: string }) =>
                primaryLanguage.l_code === language
            )
        )
      ) {
        setDisableApplyButton(true);
        setShowNotification(true);
      } else {
        setDisplayLanguage();
        setContentLanguage();
        setShowNotification(false);
        setUpdateSelection(true);
      }
    }
  };

  useEffect(() => {
    showLanguagePopup
      ? disableBackgroundScrolling()
      : enableBackgroundScrolling();
  }, [showLanguagePopup]);

  return (
    <>
      <ButttonContainer>
        <LanguageButton
          aria-label="Change label button"
          data-tooltip="Change Language"
          onClick={() => setShowLanguagePopup(true)}
          data-testid="language-change-btn"></LanguageButton>
        <LanguageToolTip />
      </ButttonContainer>
      <Portal show={showLanguagePopup} onClick={LanguagePopupCloseHandler}>
        <Container
          onClick={(event) => {
            event.stopPropagation();
          }}
          data-testid="language-change-container">
          <LanguageHeader>
            <div
              css={style.tab}
              onClick={showContentMenu}
              data-testid="content-language-btn">
              <span
                css={[
                  style.tabText,
                  showContentLanguageSelection && style.active
                ]}>
                {languageStrings?.CONTENT_LANG}
              </span>
            </div>
            <div
              css={style.tab}
              onClick={showDisplayMenu}
              data-testid="display-language-btn">
              <span
                css={[
                  style.tabText,
                  !showContentLanguageSelection && style.active
                ]}>
                {languageStrings?.DISPLAY_LANG}
              </span>
            </div>
          </LanguageHeader>
          {showContentLanguageSelection && showNotification ? (
            <NotifyToUsePrimaryLanguages text={notificationText} />
          ) : null}
          <LanguageBody>
            {showContentLanguageSelection ? (
              <ContentLanguage
                handleChange={handleCheckBoxChange}
                selectedLanguages={selectedContentLanguages}
                showPrimaryLanguages={showNotification}
              />
            ) : (
              <DisplayLanguage
                handleChange={handleSelectionChange}
                displayLanguage={selectedDisplayLanguage}
              />
            )}
          </LanguageBody>
          <LanguageFooter>
            <OutlineButton onClick={LanguagePopupCloseHandler}>
              {languageStrings?.CANCEL}
            </OutlineButton>
            <Button
              css={[disableApplyButton && style.disableButton]}
              disabled={disableApplyButton}
              onClick={handleApplyButton}
              data-testid="apply-btn">
              {languageStrings?.APPLY}
            </Button>
          </LanguageFooter>
        </Container>
      </Portal>
    </>
  );
};

export default LanguagePopup;
